import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Helpers from "../Config/Helpers";
import { Link, useNavigate, useLocation } from "react-router-dom";
import iziToast from "izitoast";
import "izitoast/dist/css/iziToast.min.css";
import {
  Box,
  Paper,
  Stack,
  TextField,
  Typography,
  Button,
  IconButton,
  Chip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import PaymentIcon from "@mui/icons-material/Payment";
import HomeIcon from "@mui/icons-material/Home";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import PageShell from "./ModuleLayout/PageShell";
import ReusableDataGrid from "../Components/ReusableDataGrid";

function Receipts() {
  const current_url = useLocation();
  const actionSlotSx = {
    width: 36,
    height: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const toLocalDateString = (value) => {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [search, setSearch] = useState("");
  const [perms, setPerms] = useState({});
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(() =>
    toLocalDateString(new Date()),
  );

  const navigate = useNavigate();

  const getReceipts = useCallback((date) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (date) {
      params.set("date", date);
      params.set("tzOffsetMinutes", String(new Date().getTimezoneOffset()));
    }
    axios
      .get(
        `${Helpers.baseUrl}/receipts/invoices/${Helpers.authParentId}${
          params.toString() ? `?${params.toString()}` : ""
        }`,
        Helpers.headers,
      )
      .then((response) => setReceipts(response.data.reverse()))
      .catch((error) => Helpers.unauthenticated(error, navigate))
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleDelete = (receiptId) => {
    iziToast.warning({
      timeout: 20000,
      close: false,
      overlay: true,
      displayMode: "once",
      zindex: 9999,
      title: "",
      message: "Are you sure to delete this receipt",
      position: "center",
      buttons: [
        [
          "<button><b>YES</b></button>",
          function (instance, toast) {
            axios
              .get(
                `${Helpers.baseUrl}/receipts/delete/${receiptId}`,
                Helpers.headers,
              )
              .then(() => {
                getReceipts(selectedDate);
                instance.hide({ transitionOut: "fadeOut" }, toast, "button");
                Helpers.toast("success", "Receipt deleted successfully");
              })
              .catch((error) => {
                Helpers.unauthenticated(error, navigate);
                instance.hide({ transitionOut: "fadeOut" }, toast, "button");
              });
          },
          true,
        ],
        [
          "<button>NO</button>",
          function (instance, toast) {
            instance.hide({ transitionOut: "fadeOut" }, toast, "button");
          },
        ],
      ],
    });
  };

  const getPerms = useCallback(() => {
    const link = current_url.pathname;
    const permissions = JSON.parse(localStorage.getItem("permissions"));
    if (permissions) {
      const perm = permissions.find((p) => p.tab_link === link);
      setPerms(perm || {});
    }
  }, [current_url.pathname]);

  useEffect(() => {
    getPerms();
  }, [getPerms]);

  useEffect(() => {
    getReceipts(selectedDate);
  }, [getReceipts, selectedDate]);

  const canCreate =
    Helpers.authUser.user_role == null || perms.can_create === 1;
  const canUpdate =
    Helpers.authUser.user_role == null || String(perms.can_update) === "1";
  const canDelete =
    Helpers.authUser.user_role == null || perms.can_delete === 1;

  const getInvoiceDisplay = (receipt) =>
    receipt?.friendlyNumber ||
    (receipt?.invoiceNumber ? String(receipt.invoiceNumber) : "") ||
    (receipt?._id ? `INV-${receipt._id.slice(-6).toUpperCase()}` : "--");

  const getVehicleLabel = (vehicle) =>
    [vehicle?.year, vehicle?.make, vehicle?.model].filter(Boolean).join(" ");

  const filteredReceipts = receipts.filter((receipt) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    const customerName = receipt?.customer?.name?.toLowerCase() || "";
    const vehicleName = getVehicleLabel(receipt?.vehicle).toLowerCase();
    const status = receipt?.status?.toLowerCase() || "";
    const invoiceDisplay = getInvoiceDisplay(receipt).toLowerCase();
    return (
      customerName.includes(term) ||
      vehicleName.includes(term) ||
      status.includes(term) ||
      invoiceDisplay.includes(term)
    );
  });

  const statusColor = (status) => {
    if (status === "Paid") return "success";
    if (status === "Unpaid") return "error";
    return "warning";
  };

  const columns = [
    { field: "serial", headerName: "Sr. #", width: 70, sortable: false },
    {
      field: "invoiceDisplay",
      headerName: "Invoice #",
      width: 130,
      sortable: false,
    },
    {
      field: "customer",
      headerName: "Customer",
      flex: 1,
      minWidth: 150,
      valueGetter: (_, row) => row.customer?.name || "",
    },
    {
      field: "vehicle",
      headerName: "Vehicle",
      flex: 1,
      minWidth: 160,
      valueGetter: (_, row) => getVehicleLabel(row.vehicle),
    },
    {
      field: "totalPrice",
      headerName: "Total",
      width: 110,
      renderCell: ({ row }) => `$ ${Number(row.totalPrice || 0).toFixed(2)}`,
    },
    {
      field: "paid",
      headerName: "Paid",
      width: 110,
      renderCell: ({ row }) => `$ ${Number(row.paid || 0).toFixed(2)}`,
    },
    {
      field: "remaining",
      headerName: "Remaining",
      width: 110,
      renderCell: ({ row }) => `$ ${Number(row.remaining || 0).toFixed(2)}`,
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: ({ row }) => (
        <Chip
          label={row.status || "—"}
          color={statusColor(row.status)}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 180,
      sortable: false,
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 36px)",
            alignItems: "center",
            justifyItems: "center",
            columnGap: 0.5,
            height: "100%",
          }}
        >
          {row.status !== "Paid" && canUpdate ? (
            <IconButton
              size="small"
              color="warning"
              title="Make Payment"
              onClick={() =>
                navigate(`/user/unpaid-receipt/make-payment/${row._id}`)
              }
              sx={actionSlotSx}
            >
              <PaymentIcon fontSize="small" />
            </IconButton>
          ) : (
            <Box sx={actionSlotSx} />
          )}
          <IconButton
            size="small"
            color="success"
            title="View receipt"
            onClick={() =>
              navigate(`/user/receipts/receipt-details/${row._id}`)
            }
            sx={actionSlotSx}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          {canUpdate ? <IconButton size="small" color="primary" title="Edit receipt"
            onClick={() => navigate(`/user/receipts/edit/${row._id}`)} sx={actionSlotSx}>
            <EditIcon fontSize="small" />
          </IconButton> : <Box sx={actionSlotSx} />}
          {canDelete ? (
            <IconButton
              size="small"
              color="error"
              title="Delete receipt"
              onClick={() => handleDelete(row._id)}
              sx={actionSlotSx}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          ) : (
            <Box sx={actionSlotSx} />
          )}
        </Box>
      ),
    },
  ];

  const rows = filteredReceipts.map((r, i) => ({
    ...r,
    id: r._id,
    invoiceDisplay: getInvoiceDisplay(r),
    serial: i + 1,
  }));

  return (
    <PageShell
      title="Receipts"
      icon={require("../images/icons/receipt.png")}
      breadcrumbs={[
        {
          label: "Home",
          path: "/user/dashboard",
          icon: <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />,
        },
        { label: "Receipts", path: null },
      ]}
      summaryChips={
        filteredReceipts.length > 0
          ? [
              {
                label: `${filteredReceipts.length} receipt${filteredReceipts.length !== 1 ? "s" : ""}`,
              },
            ]
          : []
      }
      actions={
        canCreate ? (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            component={Link}
            to="/user/receipts/add"
            sx={{
              backgroundColor: "rgba(255,255,255,0.2)",
              backdropFilter: "blur(10px)",
              color: "white",
              border: "1px solid rgba(255,255,255,0.3)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.3)" },
              textTransform: "none",
            }}
          >
            New RO
          </Button>
        ) : null
      }
      variant="list"
    >
      <Paper
        elevation={1}
        sx={{
          borderRadius: 2,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minHeight: 0,
          mb: 2,
        }}
      >
        {/* ── Toolbar ── */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: "divider",
            background: "linear-gradient(to right, #f8f9fa 0%, #ffffff 100%)",
            flexShrink: 0,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              All Receipts
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ minWidth: { sm: "auto", md: "55%" } }}
            >
              {/* Date filter */}
              <TextField
                label="Date"
                type="date"
                size="small"
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  setSearch("");
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 160 }}
              />
              {/* Text search */}
              <TextField
                label="Search"
                size="small"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  if (selectedDate) setSelectedDate("");
                }}
                sx={{ flexGrow: 1, minWidth: 180 }}
              />
              {/* Unpaid shortcut */}
              <Button
                variant="outlined"
                size="small"
                component={Link}
                to="/user/receipts/unpaid"
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
              >
                Unpaid Receipts
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* ── Grid ── */}
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <ReusableDataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            pageSize={10}
            pageSizeOptions={[10, 25, 50, 100]}
            noRowsMessage="No receipts available for the selected date"
          />
        </Box>
      </Paper>
    </PageShell>
  );
}

export default Receipts;
